import { useState, useEffect } from 'react';
import { FaFileContract, FaShieldAlt, FaSave, FaSpinner } from 'react-icons/fa';
import useSWR from 'swr';
import Fetcher from '../../lib/Fetcher';
import HttpInterceptor from '../../lib/HttpInterceptor';
import { toast } from 'react-toastify';

function LegalDocuments() {
  const [activeTab, setActiveTab] = useState('terms_of_service');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Fetch document for editing
  const { data, error, mutate } = useSWR(
    `/api/admin/legal-documents/${activeTab}`,
    Fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  useEffect(() => {
    if (data?.data) {
      setTitle(data.data.title || '');
      setContent(data.data.content || '');
    } else {
      // Set default titles
      if (activeTab === 'terms_of_service') {
        setTitle('Terms of Service');
        setContent('');
      } else {
        setTitle('Privacy Policy');
        setContent('');
      }
    }
  }, [data, activeTab]);

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }

    if (!content.trim()) {
      toast.error('Content is required');
      return;
    }

    setIsSaving(true);
    try {
      await HttpInterceptor.post('/api/admin/legal-documents', {
        type: activeTab,
        title: title.trim(),
        content: content.trim(),
      });

      toast.success(`${activeTab === 'terms_of_service' ? 'Terms of Service' : 'Privacy Policy'} saved successfully!`);
      mutate(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save document');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  if (error) {
    return (
      <div className="p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">Failed to load documents. Please try again.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Legal Documents</h1>
          <p className="text-gray-600">Manage Terms of Service and Privacy Policy</p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => handleTabChange('terms_of_service')}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'terms_of_service'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FaFileContract className="text-lg" />
                Terms of Service
              </button>
              <button
                onClick={() => handleTabChange('privacy_policy')}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'privacy_policy'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FaShieldAlt className="text-lg" />
                Privacy Policy
              </button>
            </nav>
          </div>

          {/* Editor */}
          <div className="p-6">
            <div className="mb-6">
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                Document Title
              </label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                placeholder="Enter document title"
              />
            </div>

            <div className="mb-6">
              <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
                Document Content
              </label>
              <textarea
                id="content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={20}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary font-mono text-sm"
                placeholder="Enter document content (supports HTML formatting)"
              />
              <p className="mt-2 text-sm text-gray-500">
                You can use HTML tags for formatting. For example: &lt;p&gt; for paragraphs, &lt;h2&gt; for headings, &lt;ul&gt; and &lt;li&gt; for lists.
              </p>
            </div>


            <div className="flex justify-end gap-4">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FaSave />
                    Save Document
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Preview Section */}
        {content && (
          <div className="bg-white rounded-lg shadow-md p-6 mt-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Preview</h2>
            <div className="prose max-w-none border border-gray-200 rounded-lg p-6 bg-gray-50">
              <h1 className="text-2xl font-bold mb-4">{title}</h1>
              <div dangerouslySetInnerHTML={{ __html: content }} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default LegalDocuments;

